/**
 * VariableCompatibilityService
 *
 * Determines which VariableTypes are compatible with which StepKinds.
 * Single source of truth for screen output capabilities.
 *
 * @module v2-variable-utils
 */

import type { VariableType, StepKind } from '@segmently/schema-models/v2';
import type { StepKindOutputCapability } from '../types/variable-utils.types';
import {
  STEP_KIND_TO_VARIABLE_TYPES,
  DISPLAY_ONLY_STEP_KINDS,
  REQUIRES_VALUE_MAPPING,
  DIRECT_BINDING_STEP_KINDS,
  PER_INPUT_BINDING_STEP_KINDS,
} from '../constants/step-kind-variable-compat';

export class VariableCompatibilityService {
  /**
   * Get compatible VariableTypes for a StepKind.
   *
   * @param stepKind - V2 screen type (e.g., 'ListSinglePick')
   * @returns Array of compatible variable types, or empty for display-only screens
   */
  getCompatibleTypes(stepKind: StepKind | string): VariableType[] {
    const types = STEP_KIND_TO_VARIABLE_TYPES[stepKind];
    return types ? [...types] : [];
  }

  /**
   * Check if a StepKind can output a given VariableType.
   */
  isCompatible(stepKind: StepKind | string, variableType: VariableType): boolean {
    const types = STEP_KIND_TO_VARIABLE_TYPES[stepKind];
    return types ? types.includes(variableType) : false;
  }

  /**
   * Check if a StepKind supports output (is not display-only).
   */
  hasOutput(stepKind: StepKind | string): boolean {
    return stepKind in STEP_KIND_TO_VARIABLE_TYPES;
  }

  /**
   * Check if a StepKind is display-only (no output).
   */
  isDisplayOnly(stepKind: StepKind | string): boolean {
    return DISPLAY_ONLY_STEP_KINDS.includes(stepKind);
  }

  /**
   * Check if a StepKind requires valueMapping for its binding.
   * True for selection screens (List/Grid picks) — they store indices
   * internally but must output semantic string values.
   */
  requiresValueMapping(stepKind: StepKind | string): boolean {
    return REQUIRES_VALUE_MAPPING.includes(stepKind);
  }

  /**
   * Check if a StepKind supports direct binding (no valueMapping needed).
   * True for TextInput, WheelPicker.
   */
  supportsDirectBinding(stepKind: StepKind | string): boolean {
    return DIRECT_BINDING_STEP_KINDS.includes(stepKind);
  }

  /**
   * Check if a StepKind uses per-input variable binding (WebEmbed pattern).
   */
  usesPerInputBinding(stepKind: StepKind | string): boolean {
    return PER_INPUT_BINDING_STEP_KINDS.includes(stepKind);
  }

  /**
   * Get all StepKinds that can output a given VariableType.
   */
  getStepKindsForType(variableType: VariableType): string[] {
    const result: string[] = [];
    for (const [stepKind, types] of Object.entries(STEP_KIND_TO_VARIABLE_TYPES)) {
      if (types.includes(variableType)) {
        result.push(stepKind);
      }
    }
    return result;
  }

  /**
   * Get full output capability descriptor for a StepKind.
   */
  getOutputCapability(stepKind: StepKind | string): StepKindOutputCapability | null {
    if (!this.hasOutput(stepKind)) {
      return null;
    }
    return {
      stepKind: stepKind as StepKind,
      supportedTypes: this.getCompatibleTypes(stepKind),
      requiresValueMapping: this.requiresValueMapping(stepKind),
      supportsDirectBinding: this.supportsDirectBinding(stepKind),
    };
  }
}

/** Singleton instance */
export const variableCompatibilityService = new VariableCompatibilityService();
